import { useState } from 'react';
import { type PreconfiguredResponse } from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesAPI';
import { type SmartPhraseContext } from '@rsos/utils/SmartPhrases';
import {
  trackLocationRequestClicked,
  trackLocationRequestFailed,
  type LocationRequestPathway,
} from '../../../gainsightTrackings/locationRequestTrackings';
import { resolveTrackedLocationRequestMessage } from '../../PreconfiguredMessages/resolveLocationRequestMessage';
import { type ProtectPrefilledPhrases } from './useDoNotTranslateMessageField';

type ChatWithMessageField = {
  messageField: { setText: (text: string) => void };
};

interface UseRequestLocationArgs {
  chat: ChatWithMessageField | null | undefined;
  isConversationEnded: boolean;
  preconfiguredMessages: PreconfiguredResponse[] | undefined;
  /** The text pathway this chat sends over; required so no caller goes untracked. */
  pathwayType: LocationRequestPathway;
  conversationId: string | undefined;
  /**
   * Shields the resolved location link from translation. The dispatcher sends
   * the prefilled message from the TalkJS UI, so this is the only way the values
   * reach the send; required so no caller ships an untagged link.
   * From `useDoNotTranslateMessageField`.
   */
  protectPhrases: ProtectPrefilledPhrases;
}

export const useRequestLocation = ({
  chat,
  isConversationEnded,
  preconfiguredMessages,
  pathwayType,
  conversationId,
  protectPhrases,
}: UseRequestLocationArgs) => {
  const [locationError, setLocationError] = useState(false);

  const handleRequestLocation = async (locationData: SmartPhraseContext) => {
    setLocationError(false);

    const trackingContext = {
      pathwayType,
      callerID: locationData.callerID,
      conversationId,
    };

    // Tracked before the guard so intent is counted even when the chat isn't in
    // a state that can accept the message.
    trackLocationRequestClicked(trackingContext);

    if (!chat || isConversationEnded || !preconfiguredMessages) {
      trackLocationRequestFailed({
        ...trackingContext,
        failureReason: 'chat_unavailable',
      });
      return;
    }

    const resolvedMessage = await resolveTrackedLocationRequestMessage(
      preconfiguredMessages,
      locationData,
      trackingContext,
    );

    if (!resolvedMessage) {
      setLocationError(true);
      return;
    }

    chat.messageField.setText(resolvedMessage.body);
    protectPhrases(resolvedMessage.resolvedPhrases);
  };

  return { handleRequestLocation, locationError };
};
